import React, { useMemo } from 'react';
import {
  Briefcase,
  Building,
  Calendar,
  CreditCard,
  Earth,
  FileText,
  Flag,
  Globe,
  Hash,
  Mail,
  MapPin,
  Phone,
  ScrollText,
  User,
} from 'lucide-react';

import type {
  DeclaredDetails,
  NormalizedProvidedDetails,
  StakeholderRole,
} from '@ballerine/common';
import { getFullCountryNameByCode, STAKEHOLDER_ROLES_MAP } from '@ballerine/common';

import { DataRow } from '../../primitives';

type UnifiedProvidedDetails =
  | NormalizedProvidedDetails
  | {
      websiteUrl?: string | null;
      companyName?: string | null;
      parentCompanyName?: string | null;
      merchantId?: string | null;
      countryCode?: string | null;
      declaredDetails?: DeclaredDetails;
    }
  | null
  | undefined;

type ProvidedDetailsProps = {
  providedDetails?: UnifiedProvidedDetails;
};

const hasValue = (val: unknown): boolean => {
  if (val === null || val === undefined) return false;

  if (typeof val === 'string') return val.trim().length > 0;

  if (Array.isArray(val)) return val.length > 0;

  return true;
};

/**
 * First value that is a non-blank string. Unlike `??`, a blank/whitespace value
 * does not suppress later fallbacks (BAL-8066 country/state precedence).
 */
const firstFilled = (...values: Array<string | null | undefined>): string | undefined =>
  values.find((v): v is string => typeof v === 'string' && v.trim().length > 0);

const CategorySection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="space-y-2">
    <h4 className="text-sm font-bold text-[#0f172b]">{title}</h4>
    <div className="space-y-3 [&>*]:mb-0">{children}</div>
  </div>
);

const renderStakeholderDetails = (
  stakeholders: Array<{
    name?: string | null;
    role?: StakeholderRole | null;
    title?: string | null;
    nationality?: string | null;
    dateOfBirth?: string | null;
    cityOfBirth?: string | null;
    countryOfBirth?: string | null;
    residency?: {
      address?: string | null;
      country?: string | null;
      state?: string | null;
    } | null;
  }>,
  roleLabel: string,
) => {
  return stakeholders.map((stakeholder, i) => {
    const labelPrefix = stakeholders.length > 1 ? `${roleLabel} ${i + 1}` : roleLabel;
    const residencyCountryName = stakeholder.residency?.country
      ? getFullCountryNameByCode(stakeholder.residency.country)
      : null;
    const nationalityName = stakeholder.nationality
      ? getFullCountryNameByCode(stakeholder.nationality)
      : null;
    const countryOfBirthName = stakeholder.countryOfBirth
      ? getFullCountryNameByCode(stakeholder.countryOfBirth)
      : null;

    return (
      <React.Fragment key={`${stakeholder.role}-${stakeholder.name ?? i}`}>
        <DataRow
          label={`${labelPrefix} Name`}
          Icon={User}
          value={stakeholder.name}
          copyable
          copyValue={stakeholder.name ?? ''}
          disableTranslation
        />
        {stakeholder.title && (
          <DataRow
            label={`${labelPrefix} Title`}
            Icon={User}
            value={stakeholder.title}
            copyable
            copyValue={stakeholder.title}
          />
        )}
        {stakeholder.residency?.address && (
          <DataRow
            label={`${labelPrefix} Address`}
            Icon={MapPin}
            value={stakeholder.residency.address}
            copyable
            copyValue={stakeholder.residency.address}
          />
        )}
        {residencyCountryName && (
          <DataRow
            label={`${labelPrefix} Country`}
            Icon={Flag}
            value={residencyCountryName}
            copyable
            copyValue={residencyCountryName}
          />
        )}
        {stakeholder.residency?.state && (
          <DataRow
            label={`${labelPrefix} State`}
            Icon={MapPin}
            value={stakeholder.residency.state}
            copyable
            copyValue={stakeholder.residency.state}
          />
        )}
        {nationalityName && (
          <DataRow
            label={`${labelPrefix} Nationality`}
            Icon={Globe}
            value={nationalityName}
            copyable
            copyValue={nationalityName}
          />
        )}
        {stakeholder.dateOfBirth && (
          <DataRow
            label={`${labelPrefix} Date of Birth`}
            Icon={Calendar}
            value={stakeholder.dateOfBirth}
            copyable
            copyValue={stakeholder.dateOfBirth}
          />
        )}
        {countryOfBirthName && (
          <DataRow
            label={`${labelPrefix} Country of Birth`}
            Icon={Globe}
            value={countryOfBirthName}
            copyable
            copyValue={countryOfBirthName}
          />
        )}
        {stakeholder.cityOfBirth && (
          <DataRow
            label={`${labelPrefix} City of Birth`}
            Icon={MapPin}
            value={stakeholder.cityOfBirth}
            copyable
            copyValue={stakeholder.cityOfBirth}
          />
        )}
      </React.Fragment>
    );
  });
};

const isNormalizedProvidedDetails = (
  details: UnifiedProvidedDetails,
): details is NormalizedProvidedDetails => {
  return details !== null && details !== undefined && 'website' in details;
};

export const ProvidedDetailsContent: React.FC<ProvidedDetailsProps> = ({ providedDetails }) => {
  // Extract stakeholders with full structure including residency and title
  type StakeholderWithDetails = {
    name?: string | null;
    role?: StakeholderRole | null;
    title?: string | null;
    nationality?: string | null;
    dateOfBirth?: string | null;
    cityOfBirth?: string | null;
    countryOfBirth?: string | null;
    residency?: {
      address?: string | null;
      country?: string | null;
      state?: string | null;
    } | null;
  };

  // Group stakeholders by role - must be called before any early returns
  const stakeholdersByRole = useMemo(() => {
    if (!providedDetails) {
      return {};
    }

    const isNormalized = isNormalizedProvidedDetails(providedDetails);
    const d = isNormalized ? providedDetails : providedDetails.declaredDetails;
    const grouped: Record<string, StakeholderWithDetails[]> = {};
    const stakeholders = (d?.stakeholders ?? []) as StakeholderWithDetails[];

    for (const stakeholder of stakeholders) {
      if (stakeholder.role) {
        const roleKey = stakeholder.role;
        const roleGroup = grouped[roleKey] ?? (grouped[roleKey] = []);
        roleGroup.push(stakeholder);
      }
    }

    return grouped;
  }, [providedDetails]);

  if (!providedDetails) {
    return (
      <p className="mt-4 text-sm italic text-slate-400">
        No details were provided during report creation.
      </p>
    );
  }

  const isNormalized = isNormalizedProvidedDetails(providedDetails);

  // Type-safe extraction based on data structure
  const normalizedData = isNormalized ? providedDetails : null;

  const d = isNormalized ? providedDetails : providedDetails.declaredDetails;
  const website = isNormalized ? providedDetails.website : providedDetails.websiteUrl;
  const companyName = isNormalized
    ? providedDetails.companyName
    : (providedDetails.companyName ?? providedDetails.declaredDetails?.parentCompanyName);
  const countryCode = isNormalized
    ? providedDetails.country
    : firstFilled(providedDetails.countryCode, (d as DeclaredDetails | undefined)?.countryCode);
  const countryName = getFullCountryNameByCode(countryCode ?? '');
  const state = isNormalized
    ? providedDetails.state
    : firstFilled(
        (d as DeclaredDetails | undefined)?.state,
        providedDetails.declaredDetails?.companyRegistryCheck?.state,
      );
  const merchantId = isNormalized ? undefined : providedDetails.merchantId;

  const hasStakeholderDetails = Object.keys(stakeholdersByRole).length > 0;

  const billingDescriptors =
    (!isNormalized && (d as DeclaredDetails | undefined)?.billingDescriptors) || [];

  const lineOfBusiness = isNormalized ? normalizedData?.lineOfBusiness : null;

  const hasBasicInfo =
    hasValue(website) ||
    hasValue(companyName) ||
    hasValue(merchantId) ||
    hasValue(d?.registrationNumber) ||
    hasValue(countryName) ||
    hasValue(state);

  // Extract arrays with proper type handling - prioritize arrays over single values
  // For normalized data (company-analysis), arrays are on the root object
  // For digital-footprint, arrays are in declaredDetails
  const addresses = isNormalized ? (normalizedData?.addresses ?? null) : (d?.addresses ?? null);
  const emails = isNormalized ? (normalizedData?.emails ?? null) : (d?.emails ?? null);
  const phoneNumbers = isNormalized
    ? (normalizedData?.phoneNumbers ?? null)
    : (d?.phoneNumbers ?? null);
  const singleAddress = isNormalized ? (normalizedData?.address ?? null) : (d?.address ?? null);
  const singlePhoneNumber = isNormalized ? (normalizedData?.phoneNumber ?? null) : null;

  // Verify arrays are actually arrays (defensive check)
  const addressesArray = Array.isArray(addresses) ? addresses : null;
  const emailsArray = Array.isArray(emails) ? emails : null;
  const phoneNumbersArray = Array.isArray(phoneNumbers) ? phoneNumbers : null;

  const hasContactInfo =
    hasValue(addressesArray) ||
    hasValue(singleAddress) ||
    hasValue(emailsArray) ||
    hasValue(phoneNumbersArray) ||
    hasValue(singlePhoneNumber);

  const hasBusinessInfo =
    hasValue(d?.mcc) ||
    hasValue(d?.dba) ||
    hasValue(d?.billingDescriptor) ||
    hasValue(billingDescriptors) ||
    hasValue(lineOfBusiness);

  const icaBin = d?.icaBin;
  const icaBinName = d?.icaBinName;

  const hasAdditionalInfo =
    hasValue(d?.additionalInformation) || hasValue(icaBin) || hasValue(icaBinName);

  const hasAnyValue =
    hasBasicInfo || hasContactInfo || hasBusinessInfo || hasStakeholderDetails || hasAdditionalInfo;

  if (!hasAnyValue) {
    return (
      <p className="mt-4 text-sm italic text-slate-400">
        No details were provided during report creation.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-2">
      {/* Left Column */}
      <div className="space-y-12">
        {hasBasicInfo && (
          <CategorySection title="Basic Information">
            {hasValue(website) && (
              <DataRow
                label="Website URL"
                Icon={Globe}
                value={
                  <a
                    href={website ?? ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="notranslate text-blue-600 hover:text-blue-800 hover:underline"
                    translate="no"
                  >
                    {website}
                  </a>
                }
                copyable
                copyValue={website ?? ''}
                disableTranslation
              />
            )}
            {hasValue(companyName) && (
              <DataRow
                label={isNormalized ? 'Company Name' : 'Merchant Name'}
                Icon={Building}
                value={companyName}
                copyable
                copyValue={companyName ?? ''}
                disableTranslation
              />
            )}
            {hasValue(merchantId) && (
              <DataRow
                label="Merchant ID"
                Icon={Hash}
                value={merchantId}
                copyable
                copyValue={merchantId ?? ''}
                disableTranslation
              />
            )}
            {hasValue(d?.registrationNumber) && (
              <DataRow
                label="Registration Number"
                Icon={isNormalized ? Hash : FileText}
                value={d?.registrationNumber}
                copyable
                copyValue={d?.registrationNumber ?? ''}
                disableTranslation
              />
            )}
            {hasValue(countryName) && (
              <DataRow
                label="Country"
                Icon={Earth}
                value={countryName}
                copyable
                copyValue={countryName}
              />
            )}
            {hasValue(state) && (
              <DataRow label="State" Icon={MapPin} value={state} copyable copyValue={state ?? ''} />
            )}
          </CategorySection>
        )}

        {hasBusinessInfo && (
          <CategorySection title="Business Information">
            {hasValue(lineOfBusiness) && (
              <DataRow
                label="Line of Business"
                Icon={Briefcase}
                value={lineOfBusiness}
                copyable
                copyValue={lineOfBusiness ?? ''}
              />
            )}
            {hasValue(d?.mcc) && (
              <DataRow
                label="MCC"
                Icon={ScrollText}
                value={d?.mcc}
                copyable
                copyValue={d?.mcc ?? ''}
              />
            )}
            {hasValue(d?.dba) &&
              d?.dba?.map((dbaItem, i) => (
                <DataRow
                  key={`dba-${i}`}
                  label={d.dba!.length > 1 ? `DBA ${i + 1}` : 'DBA'}
                  Icon={Building}
                  value={dbaItem}
                  copyable
                  copyValue={dbaItem}
                />
              ))}
            {hasValue(billingDescriptors)
              ? billingDescriptors.map((desc, i) => (
                  <DataRow
                    key={`billing-${i}`}
                    label={
                      billingDescriptors.length > 1
                        ? `Billing Descriptor ${i + 1}`
                        : 'Billing Descriptor'
                    }
                    Icon={CreditCard}
                    value={desc}
                    copyable
                    copyValue={desc}
                  />
                ))
              : hasValue(d?.billingDescriptor) && (
                  <DataRow
                    label="Billing Descriptor"
                    Icon={CreditCard}
                    value={d?.billingDescriptor}
                    copyable
                    copyValue={d?.billingDescriptor ?? ''}
                  />
                )}
          </CategorySection>
        )}

        {Object.entries(stakeholdersByRole).map(([role, stakeholders]) => {
          const roleLabel =
            STAKEHOLDER_ROLES_MAP[role as StakeholderRole] ||
            role.charAt(0).toUpperCase() + role.slice(1).replaceAll('_', ' ');

          return (
            <CategorySection key={role} title={`${roleLabel} Details`}>
              {renderStakeholderDetails(stakeholders, roleLabel)}
            </CategorySection>
          );
        })}
      </div>

      {/* Right Column */}
      <div className="space-y-12">
        {hasContactInfo && (
          <CategorySection title="Contact Information">
            {/* Addresses - prioritize arrays */}
            {addressesArray && addressesArray.length > 0
              ? addressesArray.map((address, i) => (
                  <DataRow
                    key={`address-${i}`}
                    label={addressesArray.length > 1 ? `Address ${i + 1}` : 'Address'}
                    Icon={MapPin}
                    value={address}
                    copyable
                    copyValue={address}
                  />
                ))
              : singleAddress && (
                  <DataRow
                    label="Address"
                    Icon={MapPin}
                    value={singleAddress}
                    copyable
                    copyValue={singleAddress}
                  />
                )}
            {/* Emails - always use array if available */}
            {emailsArray && emailsArray.length > 0
              ? emailsArray.map((email, i) => (
                  <DataRow
                    key={`email-${i}`}
                    label={emailsArray.length > 1 ? `Email ${i + 1}` : 'Email'}
                    Icon={Mail}
                    value={email}
                    copyable
                    copyValue={email}
                    disableTranslation
                  />
                ))
              : null}
            {/* Phone Numbers - prioritize arrays */}
            {phoneNumbersArray && phoneNumbersArray.length > 0
              ? phoneNumbersArray.map((phone, i) => (
                  <DataRow
                    key={`phone-${i}`}
                    label={phoneNumbersArray.length > 1 ? `Phone ${i + 1}` : 'Phone'}
                    Icon={Phone}
                    value={phone}
                    copyable
                    copyValue={phone}
                  />
                ))
              : singlePhoneNumber && (
                  <DataRow
                    label="Phone Number"
                    Icon={Phone}
                    value={singlePhoneNumber}
                    copyable
                    copyValue={singlePhoneNumber}
                  />
                )}
          </CategorySection>
        )}

        {hasAdditionalInfo && (
          <CategorySection title="Additional Information">
            {hasValue(d?.additionalInformation) && (
              <DataRow
                label="Additional Info"
                Icon={FileText}
                value={d?.additionalInformation}
                copyable
                copyValue={d?.additionalInformation ?? ''}
              />
            )}
            {hasValue(icaBin) && (
              <DataRow
                label="ICA/BIN"
                Icon={CreditCard}
                value={icaBin}
                copyable
                copyValue={icaBin ?? ''}
                disableTranslation
              />
            )}
            {hasValue(icaBinName) && (
              <DataRow
                label="ICA/BIN Name"
                Icon={CreditCard}
                value={icaBinName}
                copyable
                copyValue={icaBinName ?? ''}
                disableTranslation
              />
            )}
          </CategorySection>
        )}
      </div>
    </div>
  );
};
